import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useTenantPath } from "@/lib/tenantPath";
import { useRole } from "@/lib/roles";
import { Search, Plus, Star, Wallet, RefreshCw } from "lucide-react";

export default function Promoters() {
  const { t } = useTranslation("promoters");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const { canEdit } = useRole();
  const [promoters, setPromoters] = useState([]);
  const [revenueMap, setRevenueMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [tag, setTag] = useState("");
  const [active, setActive] = useState("all");
  const [sort, setSort] = useState("ranking");
  const [recomputing, setRecomputing] = useState(false);

  const recomputeStats = async () => {
    if (!window.confirm("Recalcular a taxa de aceitação, taxa de resposta e nº de eventos para TODOS os promotores? Pode demorar alguns segundos.")) return;
    setRecomputing(true);
    try {
      const r = await api.post("/promoters/recompute-stats");
      window.alert(`Concluído. ${r.data?.updated || 0} promotor(es) atualizado(s).`);
      fetchData();
    } catch (err) {
      window.alert("Erro: " + (err?.response?.data?.detail || err.message));
    } finally {
      setRecomputing(false);
    }
  };

  const fetchData = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (location) params.location = location;
    if (tag) params.tag = tag;
    if (active !== "all") params.active = active === "true";
    if (sort && sort !== "revenue") params.sort = sort;
    Promise.all([
      api.get("/promoters", { params }),
      api.get("/promoters-revenue-ranking").catch(() => ({ data: {} })),
    ])
      .then(([p, r]) => { setPromoters(p.data); setRevenueMap(r.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [q, location, tag, active, sort]);

  const locations = useMemo(() => Array.from(new Set(promoters.map((p) => p.location).filter(Boolean))).sort(), [promoters]);
  const tags = useMemo(() => Array.from(new Set(promoters.flatMap((p) => p.tags || []))).sort(), [promoters]);

  // Client-side sort if revenue
  const sortedPromoters = useMemo(() => {
    if (sort !== "revenue") return promoters;
    return [...promoters].sort((a, b) => (revenueMap[b.id]?.total_invoiced || 0) - (revenueMap[a.id]?.total_invoiced || 0));
  }, [promoters, sort, revenueMap]);

  return (
    <div className="space-y-6" data-testid="promoters-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle", { count: promoters.length })}</p>
        </div>
        <Link to={tPath("/promotores/novo")} data-testid="new-promoter-button" className={`inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${canEdit ? "" : "hidden"}`}>
          <Plus className="w-4 h-4" /> {t("newPromoter")}
        </Link>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={recomputeStats}
            disabled={recomputing}
            data-testid="recompute-promoter-stats"
            className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-900 border border-transparent hover:border-zinc-200 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
            title="Recalcular taxa de aceitação, resposta e nº eventos a partir dos dados reais"
          >
            <RefreshCw className={`w-3 h-3 ${recomputing ? "animate-spin" : ""}`} />
            {recomputing ? "A recalcular…" : "Recalcular estatísticas"}
          </button>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative col-span-1 sm:col-span-2">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="promoter-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("searchPlaceholder")}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
          />
        </div>
        <select data-testid="filter-location" value={location} onChange={(e) => setLocation(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          <option value="">{t("filters.allLocations")}</option>
          {locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select data-testid="filter-tag" value={tag} onChange={(e) => setTag(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          <option value="">{t("filters.allTags")}</option>
          {tags.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
        </select>
        <select data-testid="filter-active" value={active} onChange={(e) => setActive(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
          <option value="all">{t("filters.all")}</option>
          <option value="true">{t("filters.active")}</option>
          <option value="false">{t("filters.inactive")}</option>
        </select>
        <select data-testid="sort" value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white lg:col-span-1">
          <option value="ranking">{t("sort.ranking")}</option>
          <option value="accept_rate">{t("sort.accept")}</option>
          <option value="response_rate">{t("sort.response")}</option>
          <option value="events_count">{t("sort.events")}</option>
          <option value="revenue">{t("sort.revenue")}</option>
          <option value="name">{t("sort.name")}</option>
        </select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-zinc-500">{tc("states.loading")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4">{t("table.promoter")}</th>
                  <th className="text-left py-3 px-4 hidden md:table-cell">{t("table.location")}</th>
                  <th className="text-left py-3 px-4 hidden lg:table-cell">{t("table.tags")}</th>
                  <th className="text-right py-3 px-4">{t("table.ranking")}</th>
                  <th className="text-right py-3 px-4 hidden sm:table-cell">{t("table.accept")}</th>
                  <th className="text-right py-3 px-4 hidden sm:table-cell">{t("table.response")}</th>
                  <th className="text-right py-3 px-4 hidden md:table-cell">{t("table.events")}</th>
                  <th className="text-right py-3 px-4 hidden lg:table-cell">{t("table.revenue")}</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {sortedPromoters.map((p) => (
                  <tr key={p.id} data-testid={`promoter-row-${p.id}`} onClick={() => navigate(tPath(`/promotores/${p.id}`))} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {p.photo_url ? (
                          <img src={p.photo_url} alt={p.name} className="w-9 h-9 rounded-full object-cover bg-zinc-100" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-zinc-200 text-zinc-700 text-xs font-semibold flex items-center justify-center">
                            {p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-900 truncate">{p.name}</div>
                          <div className="text-xs text-zinc-500 truncate">{p.email}</div>
                        </div>
                        {!p.active && <span className="ml-2 text-xs text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-full">{t("table.inactive")}</span>}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-zinc-700 hidden md:table-cell">{p.location || "—"}</td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(p.tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-wide font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-1 text-zinc-900 font-semibold">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        {p.ranking?.toFixed(1)}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-zinc-700 hidden sm:table-cell">{Math.round(p.accept_rate)}%</td>
                    <td className="py-3 px-4 text-right text-zinc-700 hidden sm:table-cell">{Math.round(p.response_rate)}%</td>
                    <td className="py-3 px-4 text-right text-zinc-700 hidden md:table-cell">{p.events_count}</td>
                    <td className="py-3 px-4 text-right hidden lg:table-cell" data-testid={`revenue-${p.id}`}>
                      {revenueMap[p.id] ? (
                        <div className="inline-flex items-center gap-1 text-zinc-900 font-semibold">
                          <Wallet className="w-3.5 h-3.5 text-emerald-500" />
                          €{revenueMap[p.id].total_invoiced.toFixed(0)}
                        </div>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Link to={tPath(`/promotores/${p.id}`)} onClick={(e) => e.stopPropagation()} data-testid={`view-promoter-${p.id}`} className="text-xs font-semibold text-zinc-900 hover:underline">
                        {t("table.view")}
                      </Link>
                    </td>
                  </tr>
                ))}
                {sortedPromoters.length === 0 && (
                  <tr><td colSpan={9} className="py-10 text-center text-sm text-zinc-500">{t("empty")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
